import { DAILY_COST_MONITOR_COMPLETED_EVENT } from '../constants/events';
import type { UsageDetailsData } from './openai-api';

export const DAILY_COST_MONITOR_COMPLETION_VERSION = 1 as const;
export const DAILY_COST_MONITOR_CHANNEL_NAME = 'usage-dashboard-daily-cost-monitor-v1';
export const DAILY_COST_MONITOR_SIGNAL_STORAGE_KEY = 'dailyCostMonitorCompletionSignalV1';

export interface DailyCostMonitorCompletionPayload {
  type: 'complete';
  version: typeof DAILY_COST_MONITOR_COMPLETION_VERSION;
  utcDay: string;
  completedAt: number;
  credentialRevision: string;
  providers: Partial<Record<'openai' | 'anthropic', UsageDetailsData>>;
}

export interface DailyCostMonitorCompletionSignal {
  type: 'complete-signal';
  version: typeof DAILY_COST_MONITOR_COMPLETION_VERSION;
  utcDay: string;
  completedAt: number;
  credentialRevision: string;
  nonce: string;
}

interface CompletionSubscription {
  onPayload: (payload: DailyCostMonitorCompletionPayload) => void;
  onSignal: (signal: DailyCostMonitorCompletionSignal) => void;
}

let publisherChannel: BroadcastChannel | null | undefined;

const createNonce = (): string =>
  globalThis.crypto?.randomUUID?.()
  ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getPublisherChannel = (): BroadcastChannel | null => {
  if (publisherChannel !== undefined) return publisherChannel;
  if (typeof BroadcastChannel !== 'function') {
    publisherChannel = null;
    return null;
  }
  try {
    publisherChannel = new BroadcastChannel(DAILY_COST_MONITOR_CHANNEL_NAME);
  } catch {
    publisherChannel = null;
  }
  return publisherChannel;
};

const publishFallbackSignal = (payload: DailyCostMonitorCompletionPayload): void => {
  const signal: DailyCostMonitorCompletionSignal = {
    type: 'complete-signal',
    version: DAILY_COST_MONITOR_COMPLETION_VERSION,
    utcDay: payload.utcDay,
    completedAt: payload.completedAt,
    credentialRevision: payload.credentialRevision,
    nonce: createNonce()
  };
  try {
    localStorage.setItem(DAILY_COST_MONITOR_SIGNAL_STORAGE_KEY, JSON.stringify(signal));
  } catch {
    // The same-tab CustomEvent still keeps the local dashboard current.
  }
};

export const publishDailyCostMonitorCompletion = (
  payload: DailyCostMonitorCompletionPayload
): void => {
  window.dispatchEvent(new CustomEvent(DAILY_COST_MONITOR_COMPLETED_EVENT, { detail: payload }));
  const channel = getPublisherChannel();
  if (channel) {
    try {
      channel.postMessage(payload);
    } catch {
      // The storage signal below remains available to every other tab.
    }
  }
  // Always emit the lightweight signal because BroadcastChannel construction
  // can fail independently in a receiving tab.
  publishFallbackSignal(payload);
};

const parseSignal = (raw: string | null): DailyCostMonitorCompletionSignal | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DailyCostMonitorCompletionSignal>;
    return parsed.type === 'complete-signal'
      && parsed.version === DAILY_COST_MONITOR_COMPLETION_VERSION
      && typeof parsed.utcDay === 'string'
      && Number.isFinite(parsed.completedAt)
      && typeof parsed.credentialRevision === 'string'
      && typeof parsed.nonce === 'string'
      ? parsed as DailyCostMonitorCompletionSignal
      : null;
  } catch {
    return null;
  }
};

export const subscribeDailyCostMonitorCompletion = ({
  onPayload,
  onSignal
}: CompletionSubscription): (() => void) => {
  const handleLocalEvent = (rawEvent: Event) => {
    onPayload((rawEvent as CustomEvent<DailyCostMonitorCompletionPayload>).detail);
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== DAILY_COST_MONITOR_SIGNAL_STORAGE_KEY) return;
    const signal = parseSignal(event.newValue);
    if (signal) onSignal(signal);
  };

  window.addEventListener(DAILY_COST_MONITOR_COMPLETED_EVENT, handleLocalEvent);
  window.addEventListener('storage', handleStorage);

  let subscriberChannel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel === 'function') {
    try {
      subscriberChannel = new BroadcastChannel(DAILY_COST_MONITOR_CHANNEL_NAME);
      subscriberChannel.addEventListener('message', event => {
        onPayload(event.data as DailyCostMonitorCompletionPayload);
      });
    } catch {
      subscriberChannel = null;
    }
  }

  return () => {
    window.removeEventListener(DAILY_COST_MONITOR_COMPLETED_EVENT, handleLocalEvent);
    window.removeEventListener('storage', handleStorage);
    subscriberChannel?.close();
  };
};

export const resetDailyCostMonitorCompletionTransportForTests = (): void => {
  publisherChannel?.close();
  publisherChannel = undefined;
};
